/**
 * What the document adds up to, as the operator types.
 *
 * The server recomputes all of this on save and its answer is the one that is
 * stored — these are here so the person quoting sees the grand total move while
 * they are still deciding the rate. Both sides run the same arithmetic in the
 * same order, so the two agree to the paisa.
 */

/** A money field a person typed: "1,20,000.50" and "" both have to work. */
export function num(value) {
  const parsed = parseFloat(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

export const round2 = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;

export const lineAmount = (item) => round2(num(item?.quantity) * num(item?.rate));

const sumAmounts = (rows) => round2((rows || []).reduce((sum, row) => sum + num(row?.amount), 0));

/**
 * The contract's totals block, from the form state.
 * taxable includes the plate and die charges, so GST is charged on them too.
 */
export function calcTotals(doc) {
  const itemsSubtotal = round2((doc?.line_items || []).reduce((sum, item) => sum + lineAmount(item), 0));
  const plateTotal = sumAmounts(doc?.plate_charges);
  const dieTotal = sumAmounts(doc?.die_charges);
  const taxable = round2(itemsSubtotal + plateTotal + dieTotal);
  const gstAmount = round2(taxable * num(doc?.gst_rate) / 100);
  const freight = round2(doc?.freight);
  return {
    items_subtotal: itemsSubtotal,
    plate_total: plateTotal,
    die_total: dieTotal,
    taxable,
    gst_amount: gstAmount,
    freight,
    grand_total: round2(taxable + gstAmount + freight),
  };
}

/** "12,34,567.00" — Indian grouping, always two decimals. */
export const money = (value) =>
  num(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const rupees = (value) => `₹ ${money(value)}`;

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen',
  'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

/** Indian numbering — thousand, lakh, crore. Ported from the printed document. */
export function words(value) {
  const n = Math.round(num(value));
  if (!n) return 'Zero';
  if (n < 20) return ONES[n];
  if (n < 100) return TENS[Math.floor(n / 10)] + (n % 10 ? ` ${ONES[n % 10]}` : '');
  if (n < 1000) return `${ONES[Math.floor(n / 100)]} Hundred${n % 100 ? ` ${words(n % 100)}` : ''}`;
  if (n < 100000) return `${words(Math.floor(n / 1000))} Thousand${n % 1000 ? ` ${words(n % 1000)}` : ''}`;
  if (n < 10000000) return `${words(Math.floor(n / 100000))} Lakh${n % 100000 ? ` ${words(n % 100000)}` : ''}`;
  return `${words(Math.floor(n / 10000000))} Crore${n % 10000000 ? ` ${words(n % 10000000)}` : ''}`;
}

/** The line that goes on the document; an empty document says nothing. */
export function amountInWords(grandTotal) {
  const n = Math.round(num(grandTotal));
  return n > 0 ? `Rupees ${words(n)} Only` : '—';
}
